/**
 * ReauthRequired - UI for explicit re-authentication (keep in sync with platform-auth).
 * AADSTS160021 / silent iframe: manual actions only — auto-logout caused redirect churn loops.
 */

import React from 'react';
import { useMsal } from '@azure/msal-react';
import { authMethods } from 'utils/msalConfig';
import { performAutoLogout } from 'utils/authRecovery';
import { useTranslation } from 'utils/i18n';

const ReauthRequired = ({ error, onDismiss }) => {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const [isReauthenticating, setIsReauthenticating] = React.useState(false);
  const [isResetting, setIsResetting] = React.useState(false);

  const isSilentIframeError = error?.isSilentIframeError ||
    error?.message?.includes('AADSTS160021') ||
    error?.originalError?.errorMessage?.includes('AADSTS160021');

  const handleReauthenticate = async () => {
    if (!instance || isReauthenticating) return;
    try {
      setIsReauthenticating(true);
      await authMethods.login(instance, { prompt: 'select_account' });
    } catch (e) {
      console.error('[ReauthRequired] Failed to initiate re-authentication:', e);
      setIsReauthenticating(false);
    }
  };

  const handleResetDevice = async () => {
    if (!instance || isResetting) return;
    try {
      setIsResetting(true);
      performAutoLogout('user_reset_request');
    } catch (e) {
      console.error('[ReauthRequired] Failed to reset device login:', e);
      setIsResetting(false);
    }
  };

  if (isSilentIframeError) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}
      >
        <div
          style={{
            background: 'var(--bg-secondary, #1a1d23)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '480px',
            width: '100%',
            textAlign: 'center'
          }}
        >
          <h2 style={{ margin: '0 0 12px 0', fontSize: '20px' }}>
            {t('reauthRequired.reauthTitle')}
          </h2>
          <p style={{ margin: '0 0 20px 0', fontSize: '15px', opacity: 0.9, lineHeight: 1.5 }}>
            {t(
              'reauthRequired.silentSessionExpired',
              'Your sign-in session could not be renewed silently. Sign in again, or reset device login if the problem persists.'
            )}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button
              type="button"
              onClick={handleReauthenticate}
              disabled={isReauthenticating || isResetting || !instance}
              style={{
                padding: '10px 20px',
                background: '#4a90e2',
                border: 'none',
                borderRadius: '6px',
                color: '#fff',
                cursor: (isReauthenticating || !instance) ? 'not-allowed' : 'pointer',
                fontWeight: 600
              }}
            >
              {isReauthenticating ? t('reauthRequired.redirecting') : t('reauthRequired.signInAgain')}
            </button>
            <button
              type="button"
              onClick={handleResetDevice}
              disabled={isReauthenticating || isResetting || !instance}
              style={{
                padding: '8px 16px',
                background: 'transparent',
                border: '1px solid rgba(255, 107, 107, 0.3)',
                borderRadius: '6px',
                color: '#ff6b6b',
                cursor: (isResetting || !instance) ? 'not-allowed' : 'pointer',
                fontSize: '13px'
              }}
            >
              {isResetting ? t('reauthRequired.resetting') : t('reauthRequired.resetDeviceLogin')}
            </button>
            {onDismiss && (
              <button
                type="button"
                onClick={onDismiss}
                style={{
                  padding: '8px',
                  background: 'transparent',
                  border: 'none',
                  color: 'inherit',
                  opacity: 0.7,
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {t('reauthRequired.cancel')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px'
      }}
    >
      <div
        style={{
          background: 'var(--bg-secondary, #1a1d23)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          padding: '32px',
          maxWidth: '480px',
          width: '100%',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
        }}
      >
        <h2 style={{ margin: '0 0 16px 0', fontSize: '24px', fontWeight: '600' }}>
          {t('reauthRequired.reauthTitle')}
        </h2>
        <p style={{ margin: '0 0 24px 0', fontSize: '16px', lineHeight: '1.5', opacity: 0.9 }}>
          {t('reauthRequired.reauthBody')}
        </p>
        {error && (
          <details style={{ margin: '0 0 24px 0', fontSize: '14px', opacity: 0.7 }}>
            <summary style={{ cursor: 'pointer', marginBottom: '8px' }}>{t('reauthRequired.technicalDetails')}</summary>
            <pre style={{
              background: 'rgba(0, 0, 0, 0.3)',
              padding: '12px',
              borderRadius: '6px',
              overflow: 'auto',
              fontSize: '12px',
              maxHeight: '200px'
            }}>
              {error.message || JSON.stringify(error, null, 2)}
            </pre>
          </details>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            {onDismiss && (
              <button
                type="button"
                onClick={onDismiss}
                disabled={isReauthenticating || isResetting}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  color: 'inherit',
                  cursor: (isReauthenticating || isResetting) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: (isReauthenticating || isResetting) ? 0.5 : 1
                }}
              >
                {t('reauthRequired.cancel')}
              </button>
            )}
            <button
              type="button"
              onClick={handleReauthenticate}
              disabled={isReauthenticating || isResetting || !instance}
              style={{
                padding: '10px 20px',
                background: '#4a90e2',
                border: 'none',
                borderRadius: '6px',
                color: '#ffffff',
                cursor: (isReauthenticating || isResetting || !instance) ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: (isReauthenticating || isResetting || !instance) ? 0.5 : 1
              }}
            >
              {isReauthenticating ? t('reauthRequired.redirecting') : t('reauthRequired.signInAgain')}
            </button>
          </div>
          <div style={{
            paddingTop: '12px',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            fontSize: '14px',
            opacity: 0.7
          }}>
            <button
              type="button"
              onClick={handleResetDevice}
              disabled={isReauthenticating || isResetting || !instance}
              style={{
                padding: '8px 16px',
                background: 'transparent',
                border: '1px solid rgba(255, 107, 107, 0.3)',
                borderRadius: '6px',
                color: '#ff6b6b',
                cursor: (isReauthenticating || isResetting || !instance) ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                width: '100%',
                opacity: (isReauthenticating || isResetting || !instance) ? 0.5 : 1
              }}
            >
              {isResetting ? t('reauthRequired.resetting') : t('reauthRequired.resetDeviceLogin')}
            </button>
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', opacity: 0.6 }}>{t('reauthRequired.resetDeviceHint')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReauthRequired;
